import React, { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase_client';
import { useCompany } from '../contexts/CompanyContext';
import { useLocation, useNavigate } from 'react-router-dom';

interface SignInPageProps {
  onSignInStart?: () => void;
}

export default function SignInPage({ onSignInStart }: SignInPageProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [msgType, setMsgType] = useState<'error' | 'success' | 'email-confirmation'>('error');
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const { setSelectedCompany } = useCompany()

  // ✅ Function to resend confirmation email
  const handleResendConfirmation = async () => {
    if (!email) {
      setMsg('Please enter your email address first.');
      setMsgType('error');
      return;
    }

    setResendingEmail(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) throw error;

      setMsg('Confirmation email sent! Please check your inbox and spam folder.');
      setMsgType('success');
    } catch (err: any) {
      setMsg(`Failed to resend email: ${err.message}`);
      setMsgType('error');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMsg(null);
    setMsgType('error');
    
    // Notify parent that sign-in is starting
    if (onSignInStart) {
      onSignInStart();
    }
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        // ✅ Set error message based on error type
        let errorMessage = 'Sign in failed. Please try again.';
        
        if (error.message.includes('Invalid login credentials') || 
            error.message.includes('Email not confirmed') ||
            error.message.includes('Invalid password') ||
            error.message.includes('Wrong password')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message.includes('email_not_confirmed')) {
          errorMessage = 'Please confirm your email address before signing in.';
          setMsgType('email-confirmation');
        } else {
          errorMessage = error.message || 'An error occurred during sign in.';
        }
        
        setMsg(errorMessage);
        setMsgType('error');
        return;
      }
  
      // After successful sign-in, check for pending company data
      const pendingCompany = localStorage.getItem('pendingCompany');
  
      if (pendingCompany) {
        // ... existing pending company logic ...
      }

      // Standard redirect: if ProtectedRoute sent us here, go back after login
      const redirectTo = (location.state as any)?.from as string | undefined;

      // Fetch user's companies and navigate
      await fetchUserCompaniesAndNavigate(redirectTo);

  } catch (err: any) {
    // ✅ Handle unexpected errors
    console.error('Sign in error:', err);
    setMsg(err?.message || 'An unexpected error occurred. Please try again.');
    setMsgType('error');
  } finally {
    setLoading(false);
  }
};
  
  // In SignInPage.tsx, update the fetchUserCompaniesAndNavigate function:
  const fetchUserCompaniesAndNavigate = useCallback(async (redirectTo?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No user after sign-in');
        return;
      }

      // ✅ Check if user has invitation metadata (from Supabase invite)
      const metadata = user.user_metadata || {};
      const hasInvitationMetadata = metadata.invitation_id && metadata.org_id && metadata.role_id;

      if (hasInvitationMetadata) {
        // Check if user profile exists
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('id', user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Error checking profile:', profileError);
          // Continue with normal flow if error
        } else if (!profile) {
          // User has invitation metadata but no profile - redirect to complete profile
          console.log('User has invitation but no profile, redirecting to complete-profile');
          navigate(`/complete-profile?invitation=${metadata.invitation_id}`, { replace: true });
          return;
        }
      }

      // Fetch user's organizations
      const { data: memberships, error: membershipError } = await supabase
        .from('organization_members')
        .select('org_id')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (membershipError) {
        console.error('Membership error:', membershipError);
        throw membershipError;
      }

      if (!memberships || memberships.length === 0) {
        // ✅ Check user_invitations when no orgs found
        const { data: invitations, error: invitationError } = await supabase
          .from('user_invitations')
          .select('id, status, org_id, role_id')
          .eq('email', user.email || '')
          .order('invited_at', { ascending: false })
          .limit(1);
      
        if (invitationError) {
          console.error('Error checking invitations:', invitationError);
        }
      
        if (invitations && invitations.length > 0) {
          const invitation = invitations[0];
          
          if (invitation.status === 'pending') {
            // New user with pending invitation - redirect to complete profile
            console.log('User has pending invitation, redirecting to complete-profile');
            navigate(`/complete-profile?invitation=${invitation.id}`, { replace: true });
            return;
          } else if (invitation.status === 'accepted') {
            // User was removed from org after accepting - redirect to no-organization
            console.log('User has accepted invitation but no org access, redirecting to no-organization');
            navigate('/no-organization', { replace: true });
            return;
          }
        }
      
        // If user has invitation metadata but no memberships, redirect to complete profile
        if (hasInvitationMetadata) {
          console.log('User has invitation but no memberships, redirecting to complete-profile');
          navigate(`/complete-profile?invitation=${metadata.invitation_id}`, { replace: true });
          return;
        }
        
        // No invitations found - show error
        setMsg('No organizations found. Please contact support.');
        setMsgType('error');
        return;
      }

      // Get organization details
      const orgIds = memberships.map(m => m.org_id);
      const { data: organizations, error } = await supabase
        .from('organizations')
        .select('id, name, country_iso, address')
        .in('id', orgIds);

      if (error) {
        console.error('Organizations error:', error);
        throw error;
      }

      // Transform to companies format
      const companies = organizations?.map(org => ({
        company_id: org.id,
        company_name: org.name,
        country_iso: org.country_iso,
        address: org.address
      })) || [];

      // Helper: try to extract a companyId from known routes
      const extractCompanyIdFromPath = (path: string): string | null => {
        // Supports: /all-filings/:companyId, /settings/:companyId, /team/:companyId, /integration/:companyId, /usage-logs/:companyId
        const patterns = [
          /^\/all-filings\/([^/]+)/,
          /^\/settings\/([^/]+)/,
          /^\/team\/([^/]+)/,
          /^\/integration\/([^/]+)/,
          /^\/usage-logs\/([^/]+)/,
          /^\/company-profile\/([^/]+)/
        ];
        for (const p of patterns) {
          const m = path.match(p);
          if (m && m[1]) return m[1];
        }
        return null;
      };

      // Select company and navigate
      if (companies.length > 0) {
        const redirectCompanyId = redirectTo ? extractCompanyIdFromPath(redirectTo) : null;
        const targetCompany =
          (redirectCompanyId ? companies.find(c => c.company_id === redirectCompanyId) : null) || companies[0];

        setSelectedCompany(targetCompany);

        // If we have an explicit redirect path, go there. Otherwise default to filings.
        const targetPath = redirectTo || `/all-filings/${targetCompany.company_id}`;
        navigate(targetPath, { replace: true });
      } else {
        setMsg('No companies found. Please contact support.');
        setMsgType('error');
      }

    } catch (error) {
      console.error('Error fetching companies:', error);
      setMsg('Error loading your companies. Please try again.');
      setMsgType('error');
    }
  }, [navigate, setSelectedCompany]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Top Banner Announcement - Full Width */}
      <div className="w-full bg-blue-600 text-white py-3 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-center space-x-2 text-center">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium">
            <strong>For existing mbrs.ai users:</strong> Please click on{' '}
            <a href="/Forgot-Password" className="font-semibold underline hover:text-blue-100">
              Forgot password
            </a>
            {' '}to retrieve your password for login.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="bg-white shadow-lg rounded-xl w-full max-w-md p-8">
          <div className="flex justify-center mb-6">
          <img 
            src="/images/logo-dark.png" 
            alt="Company Logo" 
            style={{ maxWidth: '200px', height: 'auto' }}
          />
        </div>
         {/* <h2 className="text-2xl font-semibold text-center text-gray-800 mb-3">Sign In</h2> */}
         {/* <p className="text-center text-gray-500 mb-6">Access your account to continue</p> */}

        {/* ✅ Enhanced error message display */}
        {msg && (
          <div className={`mb-4 rounded-lg p-4 ${
            msgType === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : msgType === 'email-confirmation'
              ? 'bg-amber-50 border-2 border-amber-300'
              : 'bg-red-50 border border-red-200'
          }`}>
            {msgType === 'email-confirmation' ? (
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 mb-1">Email Confirmation Required</h3>
                    <p className="text-sm text-amber-800 mb-3">
                      Your email address hasn't been confirmed yet. Please check your inbox for the confirmation email we sent to <strong>{email}</strong>.
                    </p>
                    <div className="bg-amber-100 rounded p-3 mb-3">
                      <p className="text-xs text-amber-900 font-medium mb-1">📧 What to do:</p>
                      <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside">
                        <li>Check your inbox (and spam/junk folder)</li>
                        <li>Look for an email from us with subject "Confirm your signup"</li>
                        <li>Click the confirmation link in that email</li>
                        <li>Come back here and sign in again</li>
                      </ol>
                    </div>
                    <button
                      onClick={handleResendConfirmation}
                      disabled={resendingEmail}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {resendingEmail ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>Resend Confirmation Email</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className={`text-sm ${
                msgType === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {msg}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSignIn} className="space-y-5">
          <div>
            <label className="block text-gray-700 mb-1 font-medium">Email</label>
            <input
              type="email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-1 font-medium">Password</label>
            <input
              type="password"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="flex justify-between items-center">
            <label className="flex items-center text-gray-700 text-sm">
              <input type="checkbox" className="mr-2" />
              Remember me
            </label>
            <a href="/Forgot-Password" className="text-blue-600 hover:underline text-sm">
              Forgot password?
            </a>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-gray-500 mt-6 text-sm">
          Don't have an account?{' '}
          <a href="/signup" className="text-blue-600 hover:underline font-medium">
            Sign up
          </a>
        </p>
        </div>
      </div>
    </div>
  );
}